import { useState } from "react";
import { DeltaStatic } from "react-quill-new";
import { Box, Button, FormLabel, Paper, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";

import { theme } from "@shared/lib/styles/theme";
import { BasicEditor, ActionButton } from "@/components";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

import { CreateCourseInquiryRequestDtoContent } from "@shared/generated/api/model";

interface WriteInquiryProps {
  onSubmit: (formData: {
    subject: string;
    content: CreateCourseInquiryRequestDtoContent;
  }) => Promise<void>;
  onClose: () => void;
}

export const WriteInquiry = ({ onSubmit, onClose }: WriteInquiryProps) => {
  const { t } = useCustomTranslation();
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState<DeltaStatic | null>(null);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject || !content) return;

    await onSubmit({
      subject,
      content: content as any,
    });
  };

  const handleEditorContent = (newContent: DeltaStatic) => {
    setContent(newContent);
  };

  return (
    <StyledPaper elevation={0}>
      <form
        onSubmit={handleFormSubmit}
        style={{ flex: 1 }}
      >
        <FormBox>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormLabel
              required
              sx={{
                "&.Mui-required .MuiFormLabel-asterisk": {
                  color: "error.main",
                },
                fontWeight: 500,
                color: theme.colors.text.high.black,
              }}
            >
              {t("title")}
            </FormLabel>
            <TextField
              fullWidth
              required
              placeholder={t("please_enter_a_title")}
              variant="outlined"
              onChange={(e) => setSubject(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.paper",
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormLabel
              required
              sx={{
                "&.Mui-required .MuiFormLabel-asterisk": {
                  color: "error.main",
                },
                fontWeight: 500,
                color: theme.colors.text.high.black,
              }}
            >
              {t("content")}
            </FormLabel>
            <BasicEditor
              placeholder={t("please_enter_content")}
              onEditorContent={handleEditorContent}
              showSubmitButton={false}
              readOnly={false}
              showToolbar={true}
              isEditing={true}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
          >
            <Button
              onClick={onClose}
              sx={{
                color: theme.colors.text.high.black,
                border: "1px solid #D2D2D2",
                borderRadius: 2,
                padding: "8px 16px",
                fontWeight: 700,
              }}
            >
              {t("cancel")}
            </Button>
            <ActionButton
              type="submit"
              variant="contained"
            >
              {t("register")}
            </ActionButton>
          </Box>
        </FormBox>
      </form>
    </StyledPaper>
  );
};

const StyledPaper = styled(Paper)({
  minHeight: "100vh",
  letterSpacing: -0.4,
  marginTop: 8,
});

const FormBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  flex: 1,
});
